import React, { memo } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { useGame } from '../../context/GameContext';
import { useAccessibility } from '../../context/AccessibilityContext';
import { Department } from '../../data/colombiaDepartments';
import { TouchFeedback } from '../ui/TouchFeedback';
import { BREAKPOINTS } from '../../constants/responsive';
import {
  Card,
  Badge,
  colors,
  spacing,
  textStyles
} from '../../design-system';

// Ultra-compact mini chip for maximum map space
// Mobile-optimized with 44px minimum height
// Memoized to prevent unnecessary re-renders
const DraggableChip = memo(({ department, isMobile = false }: { department: Department; isMobile?: boolean }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: department.id,
    data: department,
  });
  const { getRegionColor } = useAccessibility();

  // Dynamic background color based on accessibility settings
  const backgroundColor = getRegionColor(department.region);

  // Style for dragging and normal state
  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: 9999,
    backgroundColor: backgroundColor,
  } : {
    backgroundColor: backgroundColor,
  };

  // All our WCAG AAA colors are designed to work with white text (7:1+ contrast)
  // Use dark border for better visibility
  const borderColor = colors.gray[700]; // Dark border for good contrast
  const textColor = colors.text.inverse; // White text for all backgrounds

  // Handle keyboard events properly
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // For keyboard navigation, we want Tab + Enter to work
    // Only prevent default space scrolling
    if (e.key === ' ') {
      e.preventDefault(); // Prevent page scroll
    }
    // Don't block Enter - let it trigger either keyboard nav or drag
  };

  const chipElement = (
    <Badge
      ref={setNodeRef}
      style={{
        ...style,
        backgroundColor: backgroundColor,
        borderColor: borderColor,
        borderWidth: '2px',
        borderStyle: 'solid',
        color: textColor,
        cursor: 'move',
        userSelect: 'none',
        fontSize: textStyles.caption.fontSize[0],
        fontWeight: textStyles.ui.medium.fontWeight, // Bolder for better readability
        // Mobile: 44px height, 48px min-width for proper touch targets
        // Desktop: Compact size
        minHeight: isMobile ? '44px' : undefined,
        minWidth: isMobile ? '48px' : undefined,
        maxWidth: isMobile ? '120px' : '75px',
        padding: isMobile ? `${spacing[2]} ${spacing[3]}` : `${spacing[1]} ${spacing[2]}`,
        textShadow: '0 1px 2px rgba(0,0,0,0.2)' // Subtle shadow for better text readability
      }}
      {...listeners}
      {...attributes}
      variant="default"
      size={isMobile ? 'md' : 'sm'}
      className={`
        inline-flex items-center transition-all duration-150
        ${isDragging ? 'opacity-0' : 'hover:scale-105 active:scale-95'}
        ${isMobile ? 'snap-center' : ''}
      `}
      role="button"
      tabIndex={0}
      aria-label={`Arrastra ${department.name} al mapa. Capital: ${department.capital}, Región: ${department.region}`}
      aria-grabbed={isDragging}
      data-department-id={department.id}
      onKeyDown={handleKeyDown}
    >
      <span className="truncate">
        {department.name}
      </span>
    </Badge>
  );

  // Wrap with touch feedback on mobile
  if (isMobile) {
    return (
      <TouchFeedback type="tap" enabled={!isDragging}>
        {chipElement}
      </TouchFeedback>
    );
  }

  return chipElement;
}, (prev, next) => {
  // Only re-render if department ID or mobile flag changed
  return prev.department.id === next.department.id && prev.isMobile === next.isMobile;
});
DraggableChip.displayName = 'DraggableChip';

// Legacy full-size component (kept for compatibility)
// Memoized to prevent unnecessary re-renders
const DraggableDepartment = memo(({ department, compact = false }: { department: Department; compact?: boolean }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: department.id,
    data: department,
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: 9999,
  } : undefined;

  // Handle keyboard events properly
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ' ') {
      e.preventDefault(); // Prevent page scroll
    }
    // Don't block Enter - let it work for selection
  };

  return (
    <Card
      ref={setNodeRef}
      style={{
        ...style,
        background: `linear-gradient(to right, ${colors.brand[50]}, ${colors.success[50]})`,
        borderColor: colors.brand[400],
        borderWidth: '2px',
        cursor: 'move',
        padding: compact ? spacing[2] : spacing[3],
        boxShadow: isDragging ? `0 0 0 4px ${colors.brand[400]}` : undefined
      }}
      {...listeners}
      {...attributes}
      variant="outlined"
      padding="none"
      hover
      className={`
        relative group transition-all duration-200
        ${isDragging ? 'opacity-50 z-50' : ''}
        hover:scale-105
      `}
      role="button"
      tabIndex={0}
      aria-label={`Departamento ${department.name}. Capital: ${department.capital}. Región: ${department.region}. Presiona Enter para seleccionar, luego usa las flechas para mover`}
      aria-grabbed={isDragging}
      aria-describedby={`hint-${department.id}`}
      data-department-id={department.id}
      onKeyDown={handleKeyDown}
    >
      {/* Drag indicator */}
      <div
        className="absolute opacity-30 group-hover:opacity-70 transition-opacity"
        style={{
          top: spacing[1],
          right: spacing[1]
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M8 9h8M8 15h8" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      </div>

      <div
        style={{
          fontSize: textStyles.body.small.fontSize[0],
          fontWeight: textStyles.body.small.fontWeight,
          color: colors.text.primary
        }}
      >
        {department.name}
      </div>
      {!compact && (
        <>
          <div
            style={{
              fontSize: textStyles.caption.fontSize[0],
              color: colors.text.secondary,
              marginTop: spacing[1]
            }}
          >
            📍 {department.capital}
          </div>
          <div
            style={{
              fontSize: textStyles.caption.fontSize[0],
              color: colors.brand[600],
              marginTop: spacing[1],
              fontWeight: textStyles.ui.medium.fontWeight
            }}
          >
            {department.region}
          </div>
        </>
      )}
      <span id={`hint-${department.id}`} className="sr-only">
        Arrastra este departamento al mapa para colocarlo en su ubicación correcta
      </span>
    </Card>
  );
}, (prev, next) => {
  // Only re-render if department ID or compact flag changed
  return prev.department.id === next.department.id && prev.compact === next.compact;
});
DraggableDepartment.displayName = 'DraggableDepartment';

interface DepartmentTrayProps {
  layout?: 'horizontal' | 'vertical' | 'compact' | 'ultra-compact' | 'mobile-scroll';
}

export default function DepartmentTray({ layout = 'horizontal' }: DepartmentTrayProps) {
  const game = useGame();

  // Detect if we should use mobile layout (use centralized breakpoint constant)
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= BREAKPOINTS.mobile.max); // Use constant (1023px)
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Use filtered departments from game state (respects regional mode)
  const activeDepartments = game.getFilteredDepartments();

  // Filter out already placed departments
  const availableDepartments = activeDepartments.filter(
    dept => !game.placedDepartments.has(dept.id)
  );

  if (availableDepartments.length === 0) {
    return (
      <div
        className="text-center"
        style={{
          padding: `${spacing[4]} 0`,
          color: colors.text.tertiary
        }}
        role="status"
        aria-live="polite"
      >
        <div
          style={{
            fontSize: textStyles.heading.h2.fontSize[0],
            marginBottom: spacing[1]
          }}
          aria-hidden="true"
        >
          🎉
        </div>
        <div
          style={{
            fontSize: textStyles.body.small.fontSize[0],
            color: colors.text.secondary
          }}
        >
          ¡Completado!
        </div>
        <div
          style={{
            fontSize: textStyles.caption.fontSize[0],
            marginTop: spacing[1],
            color: colors.success[600],
            fontWeight: textStyles.ui.medium.fontWeight
          }}
        >
          ¡Excelente!
        </div>
        <span className="sr-only">Todos los departamentos han sido colocados correctamente</span>
      </div>
    );
  }

  // Sort departments alphabetically for easier finding
  const sortedDepartments = [...availableDepartments].sort((a, b) =>
    a.name.localeCompare(b.name, 'es')
  );

  // Group departments by region for better organization
  const regionGroups: { [key: string]: typeof sortedDepartments } = {};
  sortedDepartments.forEach(dept => {
    if (!regionGroups[dept.region]) {
      regionGroups[dept.region] = [];
    }
    regionGroups[dept.region].push(dept);
  });

  // Define logical region order (alphabetical)
  const regionOrder = ['Amazonía', 'Andina', 'Caribe', 'Insular', 'Orinoquía', 'Pacífico'];

  // Sort regions according to defined order
  const sortedRegionEntries = Object.entries(regionGroups).sort((a, b) => {
    const indexA = regionOrder.indexOf(a[0]);
    const indexB = regionOrder.indexOf(b[0]);
    // If not in order list, sort alphabetically
    if (indexA === -1 && indexB === -1) return a[0].localeCompare(b[0]);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  // Mobile horizontal scroll layout with larger touch targets
  if (layout === 'mobile-scroll' || (layout === 'compact' && isMobile)) {
    return (
      <div
        className="flex flex-col"
        style={{ gap: spacing[3] }}
        role="region"
        aria-label="Departamentos disponibles para colocar"
      >
        {/* Region groups with mobile-optimized chips */}
        {sortedRegionEntries.map(([region, depts]) => (
          <div key={region} className="flex flex-col" style={{ gap: spacing[2] }}>
            <h4
              style={{
                fontSize: textStyles.caption.fontSize[0],
                fontWeight: textStyles.ui.medium.fontWeight,
                color: colors.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                paddingLeft: spacing[2]
              }}
              id={`mobile-region-${region}`}
            >
              {region} ({depts.length})
            </h4>
            <div
              className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar pb-2"
              style={{
                gap: spacing[2],
                paddingLeft: spacing[2],
                paddingRight: spacing[2],
              }}
              role="group"
              aria-labelledby={`mobile-region-${region}`}
            >
              {depts.map(department => (
                <DraggableChip
                  key={department.id}
                  department={department}
                  isMobile={true}
                />
              ))}
            </div>
            {/* Scroll indicator dots */}
            {depts.length > 3 && (
              <div className="flex justify-center gap-1">
                {Array.from({ length: Math.ceil(depts.length / 3) }).map((_, i) => (
                  <div
                    key={i}
                    className="w-1.5 h-1.5 rounded-full bg-gray-300"
                    aria-hidden="true"
                  />
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Hide scrollbar styling */}
        <style>{`
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>
      </div>
    );
  }

  // Compact chip layout for minimal space usage (desktop)
  if (layout === 'compact') {
    return (
      <div
        className="flex flex-col"
        style={{ gap: spacing[3] }}
        role="region"
        aria-label="Departamentos disponibles para colocar"
      >
        {/* Region groups with compact chips */}
        {sortedRegionEntries.map(([region, depts]) => (
          <div key={region} className="flex flex-col" style={{ gap: spacing[1.5] }}>
            <h4
              style={{
                fontSize: textStyles.caption.fontSize[0],
                fontWeight: textStyles.ui.medium.fontWeight,
                color: colors.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                paddingLeft: spacing[1]
              }}
              id={`region-${region}`}
            >
              {region} ({depts.length})
            </h4>
            <div
              className="flex flex-wrap"
              style={{ gap: spacing[1.5] }}
              role="group"
              aria-labelledby={`region-${region}`}
            >
              {depts.map(department => (
                <DraggableChip
                  key={department.id}
                  department={department}
                  isMobile={false}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Ultra-compact layout for maximum map space
  if (layout === 'ultra-compact') {
    return (
      <div role="region" aria-label="Departamentos disponibles para colocar">
        {/* Tiny chips grouped by region */}
        {sortedRegionEntries.map(([region, depts], index) => (
          <div
            key={region}
            style={{
              marginTop: index > 0 ? spacing[2] : '0'
            }}
          >
            <h4
              style={{
                fontSize: '10px',
                fontWeight: textStyles.ui.medium.fontWeight,
                color: colors.text.tertiary,
                textTransform: 'uppercase',
                paddingLeft: spacing[1],
                marginBottom: spacing[1]
              }}
              id={`ultra-region-${region}`}
            >
              {region}
            </h4>
            <div
              className="flex flex-wrap"
              style={{
                gap: spacing[1],
                paddingLeft: spacing[1]
              }}
              role="group"
              aria-labelledby={`ultra-region-${region}`}
            >
              {depts.map(department => (
                <DraggableChip
                  key={department.id}
                  department={department}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Vertical layout for sidebar
  if (layout === 'vertical') {
    return (
      <div
        className="flex flex-col"
        style={{ gap: spacing[4] }}
        role="region"
        aria-label="Panel de departamentos"
      >
        {/* Quick stats */}
        <Card
          variant="default"
          padding="md"
          className="text-center"
          style={{ backgroundColor: colors.brand[50] }}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <div
            style={{
              fontSize: textStyles.heading.h1.fontSize[0],
              fontWeight: textStyles.heading.h1.fontWeight,
              color: colors.brand[600]
            }}
            aria-hidden="true"
          >
            {availableDepartments.length}
          </div>
          <div
            style={{
              fontSize: textStyles.caption.fontSize[0],
              color: colors.text.secondary
            }}
          >
            Departamentos restantes
          </div>
          <span className="sr-only">{availableDepartments.length} departamentos restantes por colocar</span>
        </Card>

        {/* Departments by region */}
        {sortedRegionEntries.map(([region, depts]) => (
          <div key={region} className="flex flex-col" style={{ gap: spacing[2] }}>
            <h4
              style={{
                fontSize: textStyles.caption.fontSize[0],
                fontWeight: textStyles.ui.medium.fontWeight,
                color: colors.text.tertiary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
              id={`vert-region-${region}`}
            >
              {region}
            </h4>
            <div
              className="flex flex-col"
              style={{ gap: spacing[2] }}
              role="group"
              aria-labelledby={`vert-region-${region}`}
            >
              {depts.map(department => (
                <DraggableDepartment
                  key={department.id}
                  department={department}
                  compact={false}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Horizontal layout (original)
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
      style={{ gap: spacing[3] }}
      role="region"
      aria-label={`${availableDepartments.length} departamentos disponibles para colocar en el mapa`}
    >
      {sortedDepartments.map(department => (
        <DraggableDepartment key={department.id} department={department} />
      ))}
    </div>
  );
}